import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { 
  FaWifi, 
  FaExclamationTriangle, 
  FaSpinner, 
  FaTachometerAlt 
} from 'react-icons/fa';
import { useStateContext } from '../Views/Context/ContextProvider';

const NetworkStatus = () => {
    const [networkStatus, setNetworkStatus] = useState({
        isOnline: navigator.onLine,
        speed: 'checking',
        lastChecked: null,
        ping: 0
    });

    const {handleSetPing}=useStateContext();
    useEffect(()=>{

handleSetPing(networkStatus.ping);
    },[networkStatus.ping,handleSetPing])

    const checkNetworkSpeed = useCallback(async () => {
        // List of reliable endpoints to test network connection
        const testEndpoints = [
            {
                url: 'https://httpbin.org/status/200',
                method: 'GET'
            },
            {
                url: 'https://api.ipify.org?format=json',
                method: 'GET'
            },
            {
                url: 'https://jsonplaceholder.typicode.com/posts/1',
                method: 'GET'
            }
        ];

        let duration = 0;
        let success = false;

        // Try each endpoint until one succeeds
        for (const endpoint of testEndpoints) {
            const startTime = performance.now();
            
            try {
                setNetworkStatus(prev => ({ ...prev, speed: 'checking' }));

                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 3000);
                
                const response = await fetch(endpoint.url, {
                    method: endpoint.method,
                    cache: 'no-store',
                    mode: 'cors',
                    signal: controller.signal
                });
                
                clearTimeout(timeoutId);

                if (response.ok) {
                    duration = performance.now() - startTime;
                    success = true;
                    break;
                }
            } catch (error) {
                console.debug(`Failed to connect to ${endpoint.url}, trying next endpoint...`);
                continue;
            }
        }

        if (success) {
            let speed;
            if (duration < 500) speed = 'fast';
            else if (duration < 2000) speed = 'slow';
            else speed = 'very-slow';
            
            setNetworkStatus(prev => ({
                ...prev,
                isOnline: true,
                speed,
                ping: Math.round(duration),
                lastChecked: new Date()
            }));
        } else {
            setNetworkStatus(prev => ({
                ...prev,
                isOnline: false,
                speed: 'none',
                ping: 0,
                lastChecked: new Date()
            }));
        }
    }, []);

    useEffect(() => {
        // Initial check
        checkNetworkSpeed();
        
        // Set up regular checks every 30 seconds
        const interval = setInterval(checkNetworkSpeed, 30000);
        
        // Online/offline event listeners
        const handleOnline = () => {
            toast.dismiss();
            checkNetworkSpeed();
        };
        
        const handleOffline = () => {
            setNetworkStatus(prev => ({
                ...prev,
                isOnline: false,
                speed: 'none',
                ping: 0
            }));
            toast.warning('You are offline. Some features may not work.', {
                autoClose: false,
                closeOnClick: false,
                toastId: 'offline-warning'
            });
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            clearInterval(interval);
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
            toast.dismiss('offline-warning');
        };
    }, [checkNetworkSpeed]);

    const getStatusColor = () => {
        switch(networkStatus.speed) {
            case 'fast': return '#4CAF50'; // Green
            case 'slow': return '#FFC107'; // Amber
            case 'very-slow': return '#FF9800'; // Orange
            case 'none': return '#F44336'; // Red
            case 'checking': return '#2196F3'; // Blue
            default: return '#9E9E9E'; // Grey
        }
    };

    const getStatusIcon = () => {
        if (!networkStatus.isOnline) {
            return <FaExclamationTriangle />;
        }
        switch(networkStatus.speed) {
            case 'fast': return <FaWifi />;
            case 'slow': 
            case 'very-slow': return <FaTachometerAlt />;
            case 'none': return <FaExclamationTriangle />;
            case 'checking': return <FaSpinner className="spin" />;
            default: return <FaWifi />;
        }
    };

    const getStatusText = () => {
        if (!networkStatus.isOnline) return 'Offline';
        switch(networkStatus.speed) {
            case 'fast': return `Fast (${networkStatus.ping}ms)`;
            case 'slow': return `Slow (${networkStatus.ping}ms)`;
            case 'very-slow': return `Very Slow (${networkStatus.ping}ms)`;
            case 'none': return 'No connection';
            case 'checking': return 'Checking...';
            default: return 'Unknown';
        }
    };

    return (
        <div style={{
            
            padding: '8px 12px',
            borderRadius: '20px',
            backgroundColor: getStatusColor(),
            color: 'white',
            fontSize: '12px',
            fontWeight: 'bold',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
            transition: 'background-color 0.3s ease'
        }}>
            <span style={{ 
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center'
            }}>
                {getStatusIcon()}
            </span>
            <span>{getStatusText()}</span>
            
            <style>{`
                .spin {
                    animation: spin 1s linear infinite;
                }
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default NetworkStatus;